import { ref, onBeforeUnmount } from 'vue';

type AutoRetryOptions = {
  interval?: number; // 再試行間隔（ミリ秒）、デフォルト15秒
  maxRetries?: number; // 最大リトライ回数
  onSuccess?: () => void; // 成功時のコールバック
  onError?: (error: Error) => void; // エラー時のコールバック
  onStop?: (reason: string) => void; // 停止時のコールバック
};

export const useAutoRetry = (
  retryFunction: () => Promise<boolean>, // リトライ関数、成功時はtrue、継続が必要な場合はfalseを返す
  options: AutoRetryOptions = {},
) => {
  const {
    interval = 15000,
    maxRetries = 20, // 5分間（15秒 × 20回）
    onSuccess,
    onError,
    onStop,
  } = options;

  const isActive = ref(false);
  const retryCount = ref(0);
  const intervalId = ref<number | null>(null);

  const start = async () => {
    if (isActive.value) return;

    isActive.value = true;
    retryCount.value = 0;

    try {
      // 最初に一度retryFunctionを実行
      const initialSuccess = await retryFunction();

      if (initialSuccess) {
        onSuccess?.();
        isActive.value = false;
        return;
      }
    } catch (error) {
      console.error('Initial retry error:', error);
      onError?.(error as Error);
      stop('initial retry error occurred');
      return;
    }

    // 最初の実行が失敗した場合、定期的なリトライを開始
    intervalId.value = window.setInterval(() => {
      void (async () => {
        // 既に停止している場合は何もしない
        if (!isActive.value) {
          return;
        }

        try {
          // 最大リトライ回数をチェック
          if (retryCount.value >= maxRetries) {
            stop('max retries reached');
            return;
          }

          retryCount.value++;

          // リトライ実行
          const success = await retryFunction();

          // 実行中に停止された場合は何もしない
          if (!isActive.value) {
            return;
          }

          if (success) {
            onSuccess?.();
            stop('retry succeeded');
          }
        } catch (error) {
          // 実行中に停止された場合はエラーを無視
          if (!isActive.value) {
            return;
          }
          console.error('Auto retry error:', error);
          onError?.(error as Error);
          stop('retry error occurred');
        }
      })();
    }, interval);
  };

  const stop = (reason?: string) => {
    if (intervalId.value) {
      clearInterval(intervalId.value);
      intervalId.value = null;
    }
    if (!isActive.value) return;

    isActive.value = false;
    onStop?.(reason || 'manual stop');
  };

  onBeforeUnmount(() => {
    stop('component unmounted');
  });

  return {
    isActive,
    retryCount,
    start,
    stop,
  };
};
